"use client";

import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { RotateCcw } from "lucide-react";
import { useMobile } from "@/components/providers/MobileProvider";
import { useDeviceOrientation } from "@/hooks/useDeviceOrientation";
type OrientationLockContextValue = {
  register: () => void;
  unregister: () => void;
};
const OrientationLockContext = createContext<OrientationLockContextValue | null>(null);
export function useAllowLandscape() {
  const ctx = useContext(OrientationLockContext);
  useEffect(() => {
    if (!ctx) return;
    ctx.register();
    return () => ctx.unregister();
  }, [ctx]);
}
export function OrientationLockProvider({
  children
}: {
  children: ReactNode;
}) {
  const t = useTranslations("orientationLock");
  const isMobile = useMobile();
  const orientation = useDeviceOrientation();
  const [optOutCount, setOptOutCount] = useState(0);
  const value: OrientationLockContextValue = {
    register: () => setOptOutCount(c => c + 1),
    unregister: () => setOptOutCount(c => Math.max(0, c - 1))
  };
  const showOverlay = isMobile && orientation === "landscape" && optOutCount === 0;
  return <OrientationLockContext.Provider value={value} data-sentry-element="OrientationLockContext.Provider" data-sentry-component="OrientationLockProvider" data-sentry-source-file="OrientationLockProvider.tsx">
      {children}
      {showOverlay && <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-6 bg-primary px-8 text-center text-primary-foreground">
          <Image src="/assets/icons/logoHorizontalWhite.svg" alt="CasaStep" width={160} height={40} priority className="h-10 w-auto" />
          <RotateCcw className="h-9 w-9" />
          <div className="space-y-1">
            <p className="text-lg font-semibold">{t("title")}</p>
            <p className="text-sm text-primary-foreground/80">
              {t("description")}
            </p>
          </div>
        </div>}
    </OrientationLockContext.Provider>;
}